/**
 * Moments: record "the first time baby said hello", "the first time Dad felt it",
 * "the last kick before we met". Each is one tap + optional week, spot and note,
 * and becomes a chapter of the birth story (Wrapped).
 */
import { makeMoment, MOMENT_KINDS, MOMENT_NOTE_MAX, type Moment, type MomentKind } from '../domain/moments';
import { pregnancyInfo } from '../domain/pregnancy';
import { repo } from '../data/repo';
import { t } from '../i18n';
import { sfx } from '../audio/sfx';
import { bumpPoint, bumpSvg, momentIcon } from './art';
import { closeSheet, esc, openSheet, toast, vibrate } from './dom';

const titleKey = (k: MomentKind) => k;
const leadKey = (k: MomentKind) => (`lead_${k}`) as const;
const noteKey = (k: MomentKind) => (`note_${k}`) as const;

/** A row of the three moments, for the top of the journal sheet. */
export function momentsRowHtml(): string {
  return `<div class="moments-row">${MOMENT_KINDS.map(k => {
    const m = repo.moments.find(x => x.kind === k);
    return `<button class="moment-chip ${m ? 'done' : ''}" data-action="open-moment" data-kind="${k}">
      ${momentIcon(k, 44)}
      <span class="mc-t">${esc(t('moments', titleKey(k)))}</span>
      <span class="mc-s">${esc(m ? (m.week ? t('moments', 'savedWeek', { n: m.week }) : t('moments', 'saved')) : t('moments', 'notSet'))}</span>
    </button>`;
  }).join('')}</div>`;
}

export function openMoment(kind: MomentKind, onDone?: () => void): void {
  const existing = repo.moments.find(m => m.kind === kind);
  const p = repo.profile;
  let week: number | null = existing?.week ?? (p ? Math.max(4, Math.min(45, pregnancyInfo(p.dueDate).week)) : null);
  let spot: { x: number; y: number } | null = existing && existing.x !== null && existing.y !== null ? { x: existing.x, y: existing.y } : null;

  const render = () => {
    const box = document.getElementById('m-bump');
    if (box) box.innerHTML = bumpSvg({ foot: spot, glows: spot ? [{ x: spot.x, y: spot.y, warm: true, strength: 0.6 }] : [], width: 220, idAttr: 'm-bump-svg' });
    const w = document.getElementById('m-week'); if (w) w.textContent = week ? t('moments', 'weekN', { n: week }) : t('moments', 'weekUnknown');
    const hint = document.getElementById('m-hint'); if (hint) hint.textContent = spot ? t('moments', 'spotSet') : t('moments', 'spotHint');
  };

  openSheet(`
    <div class="moment-head">${momentIcon(kind, 64)}
      <div><h2>${esc(t('moments', titleKey(kind)))}</h2><p class="muted small">${esc(t('moments', leadKey(kind)))}</p></div></div>
    <div class="field"><label>${esc(t('moments', 'week'))}</label>
      <div class="stepper"><button class="icon-btn" data-action="m-week" data-d="-1" aria-label="-1">−</button>
        <span id="m-week" class="stepper-v"></span>
        <button class="icon-btn" data-action="m-week" data-d="1" aria-label="+1">+</button></div></div>
    <div class="field"><label>${esc(t('moments', 'spot'))}</label>
      <div id="m-bump" class="m-bump"></div><p id="m-hint" class="small muted" style="text-align:center;margin:4px 0 0"></p></div>
    <div class="field"><label for="m-note">${esc(t('journal', 'noteLabel'))}</label>
      <textarea id="m-note" class="note-input" rows="2" maxlength="${MOMENT_NOTE_MAX}" placeholder="${esc(t('moments', noteKey(kind)))}">${esc(existing?.note ?? '')}</textarea></div>
    <button class="btn btn-primary btn-block" data-action="m-save" style="margin-top:6px">${esc(t('moments', 'save'))}</button>
    ${existing ? `<button class="link-btn" data-action="m-remove" style="display:block;margin:6px auto 0">${esc(t('moments', 'remove'))}</button>` : ''}
  `, {
    'm-week': el => {
      const d = Number(el.dataset.d);
      week = Math.max(4, Math.min(45, (week ?? 20) + d));
      render();
    },
    'm-save': () => {
      const note = (document.getElementById('m-note') as HTMLTextAreaElement).value;
      const m: Moment = makeMoment(kind, week, spot, note, Date.now());
      repo.saveMoment(m);
      sfx.complete(); vibrate([20, 40, 20]);
      closeSheet(); toast(t('moments', 'savedToast'));
      onDone?.();
    },
    'm-remove': () => {
      repo.removeMoment(kind);
      closeSheet(); toast(t('moments', 'removed'));
      onDone?.();
    }
  }, { className: 'moment-sheet' });

  render();
  document.getElementById('m-bump')?.addEventListener('click', e => {
    const svg = document.getElementById('m-bump-svg') as SVGSVGElement | null;
    if (!svg) return;
    const pt = bumpPoint(svg, (e as MouseEvent).clientX, (e as MouseEvent).clientY);
    if (!pt) return;
    spot = pt; sfx.kick(1); vibrate(15); render();
  });
}
